import { useCallback, useState } from 'react';

import AMP_KEYS from '@/configs/amp-track-keys';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import { track } from '@/utils/track/track';

export type StructuredFilterQueryInterface = {
  filterQueryFormats: StructuredFilterQueryFormatT[];
  onFilterSetChange: (formats: StructuredFilterQueryFormatT[]) => void;
  clearFilter: () => void;
  resetFilter: (formats: StructuredFilterQueryFormatT[]) => void;
};

const useStructuredFilterQuery = (
  callbacks?: {
    onFilterChangeCallback?: (formats: StructuredFilterQueryFormatT[]) => void;
    clearFilterCallback?: () => void;
  },
  initialFormats?: StructuredFilterQueryFormatT[],
): StructuredFilterQueryInterface => {
  const [currFilterSetQuery, setFilterSetQuery] = useState(
    initialFormats || [],
  );

  const clearFilter = () => {
    setFilterSetQuery([]);
    callbacks?.onFilterChangeCallback?.([]);
  };

  const onFilterSetChange = (
    filterSetQuery: StructuredFilterQueryFormatT[],
  ) => {
    setFilterSetQuery(filterSetQuery);
    callbacks?.onFilterChangeCallback?.(filterSetQuery);

    // Track filter apply complete
    if (filterSetQuery.length > 0) {
      const filterTypes = [...new Set(filterSetQuery.map((f) => f.columnKey))];
      track(AMP_KEYS.FILTER_APPLY_COMPLETE, {
        filter_count: filterSetQuery.length,
        filter_types: filterTypes,
      });
    }
  };

  const onClearFilter = () => {
    clearFilter();
    callbacks?.clearFilterCallback?.();
  };

  const resetFilter = useCallback((formats: StructuredFilterQueryFormatT[]) => {
    setFilterSetQuery(formats);
  }, []);

  return {
    filterQueryFormats: currFilterSetQuery,
    onFilterSetChange,
    clearFilter: onClearFilter,
    resetFilter,
  };
};

export default useStructuredFilterQuery;

const useStructuredStorageFilterQuery = (
  storageKey: string,
  callbacks?: {
    onFilterChangeCallback?: (formats: StructuredFilterQueryFormatT[]) => void;
    clearFilterCallback?: () => void;
  },
): {
  filterQueryFormats: StructuredFilterQueryFormatT[];
  onFilterSetChange: (formats: StructuredFilterQueryFormatT[]) => void;
  clearFilter: () => void;
} => {
  const [filterQueryFormats, setFilterSetQuery, clearFilter] =
    useWorkspaceStorage<StructuredFilterQueryFormatT[]>(storageKey, []);

  const onFilterSetChange = (formats: StructuredFilterQueryFormatT[]) => {
    setFilterSetQuery(formats);
    callbacks?.onFilterChangeCallback?.(formats);
  };

  const onClearFilter = () => {
    clearFilter();
    callbacks?.clearFilterCallback?.();
  };

  return {
    filterQueryFormats,
    onFilterSetChange,
    clearFilter: onClearFilter,
  };
};

export { useStructuredStorageFilterQuery };
